import { Context, Middleware } from "@nuxt/types";
import { useAuth0 } from "@auth0/auth0-vue";
import { abortNavigation, navigateTo } from "#app";
import { useJwtStore } from "@/store/jwt";
import { setSessionData } from "~/utils/SetSessionData";
import { IsUnauthenticatedRoute } from "~/utils/IsUnauthenticatedRoute";
import { useUserStore } from "@/store/user";
import { useOrgStore } from "@/store/org";
import { until } from "@/utils/Async";
import { isSessionStale } from "@/constants/session";
import { useLoadingStore } from "@/store/loading";

export function setUserOrgFromLocalStorage() {
  const userStore = useUserStore();
  userStore.setUser(JSON.parse(localStorage.getItem("user") ?? "{}"));

  const orgStore = useOrgStore();
  orgStore.setOrg(JSON.parse(localStorage.getItem("organization") ?? "{}"));
}

// Exposed for tests to override when import.meta.client is false
let __TEST_FORCE_CLIENT = false;

export function __setTestForceClient(val: boolean) {
  __TEST_FORCE_CLIENT = val;
}

const authMiddleware: Middleware = async (context: Context) => {
  if (!import.meta.client && !__TEST_FORCE_CLIENT) return;

  const loadingStore = useLoadingStore();
  const taskId = `auth-middleware-${Date.now()}`;
  loadingStore.startTask(taskId);

  try {
    // Don't check authentication for unprotected routes
    if (IsUnauthenticatedRoute(context.path)) {
      loadingStore.endTask(taskId);
      return;
    }

    const auth0 = useAuth0();
    if (auth0.isLoading.value) {
      await until(() => !auth0.isLoading.value);
    }

    // Check for Auth0 callback errors (e.g., access_denied from Auth0 Actions/Rules)
    if (auth0.error.value) {
      // eslint-disable-next-line no-console
      console.error("Auth0 authentication error:", auth0.error.value);
      loadingStore.endTask(taskId);
      await navigateTo("/no-access", { external: true });
      return abortNavigation();
    }

    await auth0.checkSession();
    const isAuthenticated = auth0.isAuthenticated.value;

    if (!isAuthenticated) {
      // Save attempted route to localStorage before attempting to login, unless it's the '/logout' route
      // and Avoid invalid attempted route ("/") that Auth0 may set during login redirect
      if (context.fullPath !== "/logout" && context.fullPath !== "/") {
        localStorage.setItem("attemptedRoute", context.fullPath);
      }

      loadingStore.endTask(taskId);
      await auth0.loginWithRedirect();
      return abortNavigation();
    } else {
      const jwtStore = useJwtStore();

      // Disabled: session version gating (kept for future reference)
      // // Session version gating (runs before fetching new JWT)
      // const runtime = useRuntimeConfig();
      // const requiredVersion = (runtime as any).public?.REQUIRED_SESSION_VERSION; // disabled if not set
      // if (requiredVersion) {
      //   const versionCookie = useCookie<string | undefined>(
      //     SESSION_VERSION_COOKIE,
      //     {
      //       maxAge: 60 * 60 * 24 * 365,
      //       sameSite: "lax",
      //       secure: true,
      //     },
      //   );
      //   // Force exactly once per version only if a session token exists and cookie mismatch
      //   if (jwtStore.sessionJwt && versionCookie.value !== requiredVersion) {
      //     jwtStore.clearSessionJwt();
      //     // Stamp new version immediately to avoid loop on next navigation before login completes
      //     versionCookie.value = requiredVersion;
      //     await navigateTo("/logout", { external: true });
      //     return abortNavigation();
      //   }
      // }

      // Validate session if: no JWT, no user data, OR session is stale (>30 min)
      if (
        !jwtStore.sessionJwt ||
        !localStorage.getItem("user") ||
        isSessionStale()
      ) {
        const jwt = await auth0.getAccessTokenSilently();
        jwtStore.updateSessionJwt(jwt);

        const response = await setSessionData();

        if (response === "NoApplicationAccess") {
          if (context.fullPath !== "/no-access") {
            loadingStore.endTask(taskId);
            await navigateTo("/no-access", {
              external: true,
            });
          }
          return abortNavigation();
        } else if (response === "NotAuthorized") {
          if (context.fullPath !== "/expired") {
            loadingStore.endTask(taskId);
            await navigateTo("/expired", {
              external: true,
            });
          }
          return abortNavigation();
        } else {
          const attemptedRoute = localStorage.getItem("attemptedRoute");

          setUserOrgFromLocalStorage();

          if (attemptedRoute) {
            localStorage.removeItem("attemptedRoute");
            loadingStore.endTask(taskId);
            return await navigateTo(attemptedRoute);
          } else if (context.fullPath === "/") {
            loadingStore.endTask(taskId);
            return await navigateTo("/search");
          }
        }
      }
    }

    setUserOrgFromLocalStorage();

    if (context.fullPath === "/") {
      loadingStore.endTask(taskId);
      return await navigateTo("/search");
    }

    loadingStore.endTask(taskId);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error in authMiddleware", error);
    loadingStore.endTask(taskId);
    // redirect to login page
    await navigateTo("/logout", {
      external: true,
    });
  }
};

export default authMiddleware;
